import type { JsonObject, PageContext, TrackingPrivacyMode } from './contracts';
import { removeLinkerParameter } from './linker';
import { sanitizeString } from './sanitize';
import { closestSectionTrackingName } from './section-name';

const ANONYMOUS_ATTRIBUTION_PARAMETERS = new Set([
  '_bg',
  '_bt',
  '_upl_alloc',
  '_upl_exp',
  '_upl_var',
  'campaign_id',
  'creative',
  'gad_campaignid',
  'hsa_ad',
  'hsa_cam',
  'hsa_grp',
  'utm_campaign',
  'utm_content',
  'utm_id',
  'utm_medium',
  'utm_source',
  'utm_term'
]);
const PRIVATE_INTERACTION_SELECTOR = [
  'input',
  'textarea',
  'select',
  'option',
  '[contenteditable]:not([contenteditable="false"])',
  '[data-uplane-private]'
].join(',');
const INTERACTIVE_SELECTOR = 'a,button,input[type="button"],input[type="submit"],[role="button"],[data-uplane-track]';
const MAX_STRUCTURAL_SELECTOR_DEPTH = 12;
// The ingestion contract rejects the whole batch for a longer path.
const MAX_PATH_LENGTH = 2_000;

const cleanUrl = (value: string): string | null => {
  if (!value) return null;
  const url = new URL(value, window.location.href);
  // A mailto: or tel: link names an address, not a page; keep only its scheme.
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return url.protocol;
  return `${url.origin}${url.pathname}`;
};

const trackedUrl = (
  value: string,
  privacyMode: TrackingPrivacyMode,
  preserveAnonymousCampaign = false
): string | null => {
  if (!value) return null;
  const url = new URL(value, window.location.href);
  // URL.origin is "null" for an app referrer such as android-app://, and the
  // server accepts only http and https URLs.
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  const search = removeLinkerParameter(url.search);
  if (search !== url.search) url.search = search;
  if (privacyMode === 'anonymous') {
    url.hash = '';
    for (const key of Array.from(url.searchParams.keys())) {
      if (!preserveAnonymousCampaign || !ANONYMOUS_ATTRIBUTION_PARAMETERS.has(key.toLowerCase())) {
        url.searchParams.delete(key);
      }
    }
  }
  return sanitizeString(`${url.origin}${url.pathname}${url.search}${url.hash}`, 8_000);
};

/** The referrer of the current page load, as activities send it. */
export const pageReferrer = (privacyMode: TrackingPrivacyMode): string | null => (
  trackedUrl(document.referrer, privacyMode)
);

export const getPageContext = (privacyMode: TrackingPrivacyMode): PageContext => {
  const url = new URL(window.location.href);

  return {
    url: trackedUrl(url.href, privacyMode, true) ?? `${url.origin}${url.pathname}`,
    path: sanitizeString(url.pathname, MAX_PATH_LENGTH),
    title: privacyMode === 'anonymous' ? '' : sanitizeString(document.title, 500),
    referrer: pageReferrer(privacyMode)
  };
};

const escapeCss = (value: string): string => {
  if (globalThis.CSS?.escape) return globalThis.CSS.escape(value);
  return value.replace(/[^a-zA-Z0-9_-]/g, (character) => `\\${character}`);
};

export const getElementSelector = (element: Element): string => {
  const parts: string[] = [];
  let current: Element | null = element;

  while (current && parts.length < 4) {
    if (current.id) {
      parts.unshift(`#${escapeCss(current.id)}`);
      break;
    }

    const trackingName = current.getAttribute('data-uplane-track');
    if (trackingName) {
      parts.unshift(`[data-uplane-track="${escapeCss(trackingName)}"]`);
      break;
    }

    let part = current.tagName.toLowerCase();
    const classes = Array.from(current.classList).slice(0, 2);
    if (classes.length > 0) part += classes.map((name) => `.${escapeCss(name)}`).join('');

    const parentElement: Element | null = current.parentElement;
    if (parentElement) {
      const currentTagName = current.tagName;
      const siblings: Element[] = Array.from(parentElement.children).filter(
        (candidate: Element) => candidate.tagName === currentTagName
      );
      if (siblings.length > 1) part += `:nth-of-type(${siblings.indexOf(current) + 1})`;
    }

    parts.unshift(part);
    current = parentElement;
  }

  return parts.join(' > ');
};

const getStructuralElementSelector = (element: Element): string => {
  const parts: string[] = [];
  let current: Element | null = element;

  while (current && current !== document.documentElement && parts.length < MAX_STRUCTURAL_SELECTOR_DEPTH) {
    if (current === document.body) {
      parts.unshift('body');
      break;
    }

    let part = current.tagName.toLowerCase();
    const currentTagName = current.tagName;
    const parentElement: Element | null = current.parentElement;
    if (parentElement) {
      const siblings: Element[] = Array.from(parentElement.children).filter(
        (candidate: Element) => candidate.tagName === currentTagName
      );
      if (siblings.length > 1) part += `:nth-of-type(${siblings.indexOf(current) + 1})`;
    }

    parts.unshift(part);
    current = parentElement;
  }

  return parts[0] === 'body' ? parts.join(' > ') : '';
};

const isPrivateInteraction = (target: Element): boolean => Boolean(
  target.closest(PRIVATE_INTERACTION_SELECTOR)
);

const percent = (value: number, total: number, step: number): number => {
  if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) return 0;
  const clamped = Math.min(100, Math.max(0, (value / total) * 100));
  return Math.round(clamped / step) * step;
};

const viewportBucket = (): 'desktop' | 'mobile' | 'tablet' => {
  if (window.innerWidth < 640) return 'mobile';
  if (window.innerWidth < 1024) return 'tablet';
  return 'desktop';
};

const dimensionBucket = (value: number, step: number): number => {
  if (!Number.isFinite(value) || value <= 0) return step;
  return Math.max(step, Math.round(value / step) * step);
};

const heatmapProperties = (event: MouseEvent, step: number): JsonObject => {
  const documentHeight = Math.max(
    document.documentElement.scrollHeight,
    document.body?.scrollHeight ?? 0,
    window.innerHeight
  );
  return {
    xPercent: percent(event.clientX, window.innerWidth, step),
    yPercent: percent(event.clientY + window.scrollY, documentHeight, step),
    viewport: viewportBucket()
  };
};

const elementHeatmapProperties = (target: Element, event: MouseEvent, step: number): JsonObject => {
  const properties: JsonObject = {
    ...heatmapProperties(event, step),
    viewportWidthBucket: dimensionBucket(window.innerWidth, 160),
    viewportHeightBucket: dimensionBucket(window.innerHeight, 120)
  };
  const selectedElement = target.closest(INTERACTIVE_SELECTOR) ?? target;
  const selector = getStructuralElementSelector(selectedElement);
  const bounds = selectedElement.getBoundingClientRect();
  if (selector && bounds.width > 0 && bounds.height > 0) {
    properties.coordinateSpace = 'element_relative';
    properties.elementSelector = sanitizeString(selector, 500);
    properties.elementXPercent = percent(event.clientX - bounds.left, bounds.width, step);
    properties.elementYPercent = percent(event.clientY - bounds.top, bounds.height, step);
  }
  return properties;
};

export const getClickProperties = (
  target: Element,
  event: MouseEvent,
  privacyMode: TrackingPrivacyMode
): JsonObject | null => {
  if (isPrivateInteraction(target)) return null;

  if (privacyMode === 'anonymous') {
    const trackedElement = target.closest('[data-uplane-track]');
    const trackingName = trackedElement?.getAttribute('data-uplane-track');
    const properties: JsonObject = elementHeatmapProperties(target, event, 5);
    if (trackingName) properties.name = sanitizeString(trackingName, 100);
    const section = closestSectionTrackingName(target);
    if (section) properties.section = section;
    return properties;
  }

  const interactive = target.closest(INTERACTIVE_SELECTOR);
  const selectedElement = interactive ?? target;
  const properties: JsonObject = {
    element: getElementSelector(selectedElement),
    tag: selectedElement.tagName.toLowerCase(),
    ...elementHeatmapProperties(target, event, 0.1)
  };
  const label = interactive ? interactive.getAttribute('aria-label') ?? interactive.textContent ?? '' : '';
  const href = interactive instanceof HTMLAnchorElement ? cleanUrl(interactive.href) : null;
  const trackingName = interactive?.getAttribute('data-uplane-track');

  if (label.trim()) properties.label = sanitizeString(label.replace(/\s+/g, ' '), 200);
  if (href) properties.href = href;
  if (trackingName) properties.name = sanitizeString(trackingName, 100);
  const section = closestSectionTrackingName(target);
  if (section) properties.section = section;
  return properties;
};

export const getFormProperties = (
  form: HTMLFormElement,
  privacyMode: TrackingPrivacyMode
): JsonObject | null => {
  if (isPrivateInteraction(form)) return null;
  if (privacyMode === 'anonymous') {
    const trackingName = form.getAttribute('data-uplane-track');
    return trackingName ? { name: sanitizeString(trackingName, 100) } : {};
  }
  return {
    element: getElementSelector(form),
    method: form.method.toUpperCase(),
    action: cleanUrl(form.action) ?? '',
    fieldCount: form.elements.length
  };
};
